import { useQueryState } from '@dustin-web/microsite-components/src/hooks'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import error from 'next/error'
import { useEffect, useState } from 'react'
import {
  GetCategoryForFilterPageQuery,
  GetCategoryForFilterPageQueryVariables,
  QuerySearchPageArgs,
  SearchPageQuery,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetCategoryForFilterPage } from '@productsite/src/graphql/get-category-for-filter-page.graphql'
import { newProductsOnlyFacets } from '@productsite/src/components/constants'
import { ProductReferenceFragment } from '@productsite/src/graphql/generated/graphql-types'
import { Origin, Touchpoint } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { SearchPage } from '@productsite/src/graphql/search.graphql'

type InfinityScrollRenderedPage = {
  products: ProductReferenceFragment[]
  pageInUrl: number
}

type SearchPageProps = {
  isUserInitiated: boolean
  searchPhrase: string
  selectedCategoryPath: string | null
  isSmallScreen?: boolean
}
type FilterPageProps = {
  categoryId: string
  isNewProductsPage: boolean
}

type Props = {
  products: ProductReferenceFragment[]
  /**
   * @param {{strProp: string, numProp: number}} searchPageProps - Provide if infinity-scroll is used in search-page
   */
  searchPageProps?: SearchPageProps
  /**
   * @param {{strProp: string, numProp: number}} filterPageProps - Provide if infinity-scroll is used in filter-page
   */
  filterPageProps?: FilterPageProps
}
export const infinityScrollPageSize = 18

export const findProductsInInfinityScrollPages = (
  infinityScrollPages: InfinityScrollRenderedPage[]
): ProductReferenceFragment[] => {
  const products = [] as ProductReferenceFragment[]
  infinityScrollPages.map(item => {
    item?.products?.forEach(product => {
      if (
        !products.find(existing => {
          return existing.id === product.id
        })
      ) {
        products.push(product)
      }
    })
  })
  return products
}

/** @deprecated This hook will be removed in future releases. Use `useInfiniteProducts` hook instead */
export const useInfinityScroll = ({ products, searchPageProps, filterPageProps }: Props) => {
  const { queryState } = useQueryState()
  const { sortBy } = useProductListSettings()
  const [shouldFetchPreviousPages, setShouldFetchPreviousPages] = useState(false)
  const [renderedPages, setRenderedPages] = useState<InfinityScrollRenderedPage[]>([
    { pageInUrl: queryState.page, products: products },
  ] as InfinityScrollRenderedPage[])

  const { data: previousPagesDataFilterPage } = useGraphql<
    GetCategoryForFilterPageQuery,
    GetCategoryForFilterPageQueryVariables
  >(GetCategoryForFilterPage, {
    variables: {
      categoryId: filterPageProps?.categoryId ?? '',
      page: 1,
      pageSize: (renderedPages[0].pageInUrl - 1) * infinityScrollPageSize, //Get all products that are in previous pages.
      sortBy: getSortBy(sortBy),
      facets: filterPageProps?.isNewProductsPage
        ? queryState.activeFilters.concat(newProductsOnlyFacets)
        : queryState.activeFilters,
      searchPhrase: queryState.searchwithin,
    },
    context: {
      keepPreviousResult: true,
      skip: !shouldFetchPreviousPages || !filterPageProps,
    },
  })

  const { data: previousPagesDataSearch } = useGraphql<
    SearchPageQuery,
    QuerySearchPageArgs
  >(SearchPage, {
    variables: {
      isUserInitiated: searchPageProps?.isUserInitiated,
      page: 1,
      pageSize: (renderedPages[0].pageInUrl - 1) * infinityScrollPageSize, //Get all products that are in previous pages.,
      searchPhrase: searchPageProps?.searchPhrase,
      selectedCategoryPath: searchPageProps?.selectedCategoryPath,
      origin: Origin.Organic,
      touchpoint: searchPageProps?.isSmallScreen && Touchpoint.Mobile || Touchpoint.Desktop,
      facets: queryState.activeFilters,
      sortBy: getSortBy(sortBy),
    },
    context: {
      keepPreviousResult: true,
      skip: !shouldFetchPreviousPages || !searchPageProps,
    },
  })

  const previousSearchData = previousPagesDataSearch?.searchPage

  //Fill infinityScrollPages with incoming data if not previously added.
  useEffect(() => {
    const existingViewPagePage = renderedPages.find(e => {
      return e.pageInUrl === queryState.page
    })
    if (existingViewPagePage === undefined) {
      if (products) {
        setRenderedPages([...renderedPages, { pageInUrl: queryState.page, products: products }])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  //If user browses direct to other page than 1, fetch data from previous pages.
  useEffect(() => {
    if (renderedPages[0].pageInUrl > 1) {
      setShouldFetchPreviousPages(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //When filter changes, page is always set to 1 again. - clear infinityScrollPages.
  useEffect(() => {
    if (products && queryState.page === 1) {
      setRenderedPages([{ pageInUrl: queryState.page, products: products }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState.activeFilters.length, products])

  const productsToUse =
    searchPageProps !== undefined
      ? previousSearchData?.productReferences
      : previousPagesDataFilterPage?.category?.productSearch?.productReferences
  const previousProductsInPages = [
    {
      pageInUrl: 0,
      products: productsToUse ?? [],
    },
  ] as InfinityScrollRenderedPage[]
  const productsToShow = findProductsInInfinityScrollPages([
    ...previousProductsInPages,
    ...renderedPages,
  ])

  return {
    error,
    productsToShow,
  }
}

export default useInfinityScroll
